// src/Pages/TDSMaster.jsx
import { gradients } from '../utils/theme';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, TextField, Button, Grid, Divider, Autocomplete, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Snackbar, Alert } from '@mui/material';
import { getCurrentFYear } from '../utils/financialYear';

export default function TDSMaster({ id, mode, onPopupCancel, connectionString, companyId: propsCompanyId }) {
    const { user, selectedCompany, loading } = useAuth();
    const { id: routeId } = useParams();
    const recordId =
        mode === 'edit'
            ? id
            : routeId;

    const navigate = useNavigate();
    // company info
    const userId = user?.UserId || null;
    const companyId =
        (mode === 'comp' || mode === 'edit')
            ? propsCompanyId
            : selectedCompany?.CompId;

    const AppRegID = selectedCompany?.AppRegID || null;

    const fYear = getCurrentFYear();
    const branchId = selectedCompany?.BranchId;

    // ── "Select Firm Name" — party this TDS rule applies to (PartyId) ────────
    const [firmValue, setFirmValue] = useState(null);       // { id, FirmName }
    const [firmInput, setFirmInput] = useState('');
    const [firmOptions, setFirmOptions] = useState([]);
    const [firmLoading, setFirmLoading] = useState(false);
    const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);

    // ── "TDS Ledger A/c" — ledger/party account TDS is booked against (TDSPartyId) ─
    const [tdsLedgerValue, setTdsLedgerValue] = useState(null); // { id, FirmName }
    const [tdsLedgerInput, setTdsLedgerInput] = useState('');
    const [tdsLedgerOptions, setTdsLedgerOptions] = useState([]);
    const [tdsLedgerLoading, setTdsLedgerLoading] = useState(false);
    const [tdsLedgerDropdownOpen, setTdsLedgerDropdownOpen] = useState(false);

    // form fields
    const [deducteeType, setDeducteeType] = useState('');
    const [deducteeCode, setDeducteeCode] = useState('');
    const [natureOfPayment, setNatureOfPayment] = useState('');
    const [nopCode, setNopCode] = useState('');
    const [section, setSection] = useState('');
    const [tdsPer, setTdsPer] = useState('');

    // validation errors
    const [tdsPerError, setTdsPerError] = useState('');
    const [formError, setFormError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success', // success | error | warning | info
    });

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    // ─── Search "Select Firm Name" as user types, or list default options on open ──
    useEffect(() => {
        if (!firmDropdownOpen && (!firmInput || firmInput.trim() === '')) {
            setFirmOptions([]);
            return;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setFirmLoading(true);
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: 'FirmName', search: firmInput, companyId },
                });
                if (active) {
                    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                    setFirmOptions(rows);
                }
            } catch (err) {
                console.error('Error fetching firm options:', err);
                if (active) setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        }, 300);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [firmInput, firmDropdownOpen, companyId, connectionString]);

    // ─── Search "TDS Ledger A/c" as user types, or list default options on open ────
    useEffect(() => {
        if (!tdsLedgerDropdownOpen && (!tdsLedgerInput || tdsLedgerInput.trim() === '')) {
            setTdsLedgerOptions([]);
            return;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setTdsLedgerLoading(true);
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: 'FirmName', search: tdsLedgerInput, companyId },
                });
                if (active) {
                    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                    setTdsLedgerOptions(rows);
                }
            } catch (err) {
                console.error('Error fetching TDS ledger options:', err);
                if (active) setTdsLedgerOptions([]);
            } finally {
                if (active) setTdsLedgerLoading(false);
            }
        }, 300);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [tdsLedgerInput, tdsLedgerDropdownOpen, companyId, connectionString]);

    // load existing on edit
    useEffect(() => {
        if (mode === 'edit' && recordId) {
            axios.get(`${connectionString}/api/TDS/GetTDSbyId/${recordId}`)
                .then(({ data }) => {
                    setDeducteeType(data.DeducteeType || '');
                    setDeducteeCode(data.DeducteeCode || '');
                    setNatureOfPayment(data.NatureOfPayment || '');
                    setNopCode(data.NOPCode || '');
                    setSection(data.Section || '');
                    setTdsPer(data.TDSPer != null ? String(data.TDSPer) : '');

                    if (data.PartyId) {
                        const partyLabel = data.FirmName || '';
                        setFirmValue({ id: data.PartyId, FirmName: partyLabel });
                        setFirmInput(partyLabel);
                    }
                    if (data.TDSPartyId) {
                        const ledgerLabel = data.TDSLedgerName || '';
                        setTdsLedgerValue({ id: data.TDSPartyId, FirmName: ledgerLabel });
                        setTdsLedgerInput(ledgerLabel);
                    }
                })
                .catch(console.error);
        }
    }, [mode, recordId]);

    // validators
    const validatePct = (val, setErr, label) => {
        if (val === '') {
            setErr('');
        } else if (isNaN(val) || val < 0 || val > 100) {
            setErr(`Please enter ${label} between 0% to 100%`);
        } else {
            setErr('');
        }
    };

    const handleTdsPerBlur = () => validatePct(tdsPer, setTdsPerError, 'TDS %');

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!firmValue) {
            setFormError('* OOPS! Please select Firm Name.');
            return;
        }
        if (tdsPerError) {
            setFormError('* OOPS! Fill data properly.');
            return;
        }
        setFormError('');
        try {
            const payload = {
                PartyId: firmValue?.id || null,
                TDSPartyId: tdsLedgerValue?.id || null,
                TDSPer: parseFloat(tdsPer) || 0,
                DeducteeType: deducteeType,
                DeducteeCode: deducteeCode,
                NatureOfPayment: natureOfPayment,
                NOPCode: nopCode,
                Section: section,
                companyId,
                AppRegID,
                fYear,
                branchId,
                userId,
            };
            console.log('Payload', payload);
            if (mode === 'add' || mode === 'comp') {
                await axios.post(`${connectionString}/api/TDS/Insert`, payload);
                showSnackbar('Data inserted successfully', 'success');
            } else {
                await axios.put(`${connectionString}/api/TDS/Update/${recordId}`, payload);
                showSnackbar('Data updated successfully', 'success');
            }

            setTimeout(() => {
                if (onPopupCancel) {
                    onPopupCancel();
                } else {
                    navigate('/main/tdsDetail');
                }
            }, 600);
        } catch (err) {
            console.error(err);
            showSnackbar(
                `Error ${mode === 'add' || mode === 'comp' ? 'inserting' : 'updating'} data`,
                'error'
            );
        }
    };

    const handleCancel = () => {
        if (onPopupCancel) onPopupCancel();
        else navigate('/main/tdsDetail');
    };

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                handleSubmit(e);
            } else if (e.code === "KeyC") {
                e.preventDefault();
                handleCancel();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <>
            <Box sx={{ height: 4, background: gradients.primaryRow }} />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                    {mode === "add" || mode === "comp" ? "TDS Details Confirmation" : "Edit TDS Details"}
                </Typography>
            </Box>
            <Divider />
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
                    <Grid container spacing={2}>
                        {/* Column 1: Select Firm Name, TDS Ledger A/c, Deductee Code, NOP Code, TDS % */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    fullWidth
                                    options={firmOptions}
                                    loading={firmLoading}
                                    open={firmDropdownOpen}
                                    onOpen={() => setFirmDropdownOpen(true)}
                                    onClose={() => setFirmDropdownOpen(false)}
                                    getOptionLabel={(opt) => opt?.FirmName || ''}
                                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                                    value={firmValue}
                                    inputValue={firmInput}
                                    onInputChange={(_, newInput) => setFirmInput(newInput)}
                                    onChange={(_, newValue) => setFirmValue(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Select Firm Name"
                                            size="small"
                                            InputLabelProps={{ sx: requiredAsteriskStyle }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {firmLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={{ '& .MuiInputBase-root': { minHeight: 38 } }}
                                        />
                                    )}
                                />
                                <Autocomplete
                                    fullWidth
                                    options={tdsLedgerOptions}
                                    loading={tdsLedgerLoading}
                                    open={tdsLedgerDropdownOpen}
                                    onOpen={() => setTdsLedgerDropdownOpen(true)}
                                    onClose={() => setTdsLedgerDropdownOpen(false)}
                                    getOptionLabel={(opt) => opt?.FirmName || ''}
                                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                                    value={tdsLedgerValue}
                                    inputValue={tdsLedgerInput}
                                    onInputChange={(_, newInput) => setTdsLedgerInput(newInput)}
                                    onChange={(_, newValue) => setTdsLedgerValue(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="TDS Ledger A/c"
                                            size="small"
                                            InputLabelProps={{ sx: requiredAsteriskStyle }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {tdsLedgerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={{ '& .MuiInputBase-root': { minHeight: 38 } }}
                                        />
                                    )}
                                />
                                <TextField fullWidth label="Deductee Code" size="small" value={deducteeCode} onChange={e => setDeducteeCode(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                                <TextField fullWidth label="NOP Code" size="small" value={nopCode} onChange={e => setNopCode(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                                <TextField fullWidth required label="TDS %" type="number" size="small" InputLabelProps={{ sx: requiredAsteriskStyle }} value={tdsPer} onChange={e => setTdsPer(e.target.value)} onBlur={handleTdsPerBlur} error={!!tdsPerError} helperText={tdsPerError} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                            </Box>
                        </Grid>

                        {/* Column 2: Deductee Type, Nature of Payment, Section */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField fullWidth label="Deductee Type" size="small" value={deducteeType} onChange={e => setDeducteeType(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                                <TextField fullWidth label="Nature of Payment" size="small" value={natureOfPayment} onChange={e => setNatureOfPayment(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                                <TextField fullWidth label="Section" size="small" value={section} onChange={e => setSection(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                            </Box>
                        </Grid>
                    </Grid>
                    {formError && (
                        <Typography color="error" align="center" sx={{ mt: 1 }}>
                            {formError}
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleCancel} type="button" variant="outlined" color="inherit" size="small">
                        Cancel
                    </Button>
                    <Button variant="contained" type="submit" size="small">
                        {mode === 'add' || mode === "comp" ? 'Save' : 'Update'}
                    </Button>
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
